import { Injectable, inject, resource, computed } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { IGroupOptionModel, IiFirmFullGroupDataModel } from "../../settings-interfaces/ifirm-groups.models";
import { IframeMessagesService } from "../iframe-messages-service/iframe-messages.service";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import { ITranslateObject } from "@app/views-interfaces";
import { LanguageService } from "@services/index";

type GroupMethod = (includeHidden?: boolean) => IGroupOptionModel[] | undefined;

@Injectable()
export class IFirmGroupsService {
    private iframeMessagesService = inject(IframeMessagesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly translateService = inject(TranslateService);
    private readonly languageService = inject(LanguageService);
    private iFirmGroupsLoadPromise?: Promise<IiFirmFullGroupDataModel | undefined>;
    public groups: IiFirmFullGroupDataModel | undefined;

    private iFirmGroupsResource = resource<IiFirmFullGroupDataModel | undefined, undefined>({
        loader: () => this.getiFirmGroups(),
    });
    public sGroups = computed<IiFirmFullGroupDataModel | undefined>(() => this.iFirmGroupsResource.value());

    public readonly group1 = computed<GroupMethod>(() => {
        const groups = this.sGroups();
        return (includeHidden = true) => groups?.group1Options?.filter((option) => includeHidden || option.IsVisible);
    });

    public readonly group2 = computed<GroupMethod>(() => {
        const groups = this.sGroups();
        return (includeHidden = true) => groups?.group2Options?.filter((option) => includeHidden || option.IsVisible);
    });

    public readonly isGroup1JobsEnabled = computed<boolean>(() => {
        const groups = this.sGroups();
        return (groups?.group1?.enabled && groups?.group1?.group1JobsEnabled) || false;
    });

    public readonly isGroup1ContactsEnabled = computed<boolean>(() => {
        const groups = this.sGroups();
        return (groups?.group1?.enabled && groups?.group1?.group1ContactsEnabled) || false;
    });

    public readonly isGroup2JobsEnabled = computed<boolean>(() => {
        const groups = this.sGroups();
        return (groups?.group2?.enabled && groups?.group2?.group2JobsEnabled) || false;
    });

    public readonly isGroup2ContactsEnabled = computed<boolean>(() => {
        const groups = this.sGroups();
        return (groups?.group2?.enabled && groups?.group2?.group2ContactsEnabled) || false;
    });

    async loadiFirmGroups() {
        try {
            const result = await this.iframeMessagesService.postMessageResp("getiFirmGroups", {}, "gotiFirmGroups");
            if (result.status === "success") {
                this.groups = result.groups;
                this.groups?.group1Options.forEach((option) => {
                    if (option["fr-CA"] === "") {
                        option["fr-CA"] = option["en-CA"];
                    }
                    if (!option.IsVisible) {
                        option["fr-CA"] = this.translateService.instant("General.Inactive") + option["fr-CA"];
                        option["en-CA"] = this.translateService.instant("General.Inactive") + option["en-CA"];
                    }
                });
                this.groups?.group1Options.sort((a, b) =>
                    a[this.languageService.language() as keyof ITranslateObject].localeCompare(
                        b[this.languageService.language() as keyof ITranslateObject],
                    ),
                );
                this.groups?.group2Options.forEach((option) => {
                    if (option["fr-CA"] === "") {
                        option["fr-CA"] = option["en-CA"];
                    }
                    if (!option.IsVisible) {
                        option["fr-CA"] = this.translateService.instant("General.Inactive") + option["fr-CA"];
                        option["en-CA"] = this.translateService.instant("General.Inactive") + option["en-CA"];
                    }
                });
                this.groups?.group2Options.sort((a, b) =>
                    a[this.languageService.language() as keyof ITranslateObject].localeCompare(
                        b[this.languageService.language() as keyof ITranslateObject],
                    ),
                );
                return this.groups;
            }
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        } catch (error) {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
    }

    async getiFirmGroups(): Promise<IiFirmFullGroupDataModel | undefined> {
        if (!this.iFirmGroupsLoadPromise) {
            this.iFirmGroupsLoadPromise = this.loadiFirmGroups();
        }
        return this.iFirmGroupsLoadPromise;
    }

    getGroup1Enabled(): boolean | undefined {
        return this.groups?.group1?.enabled;
    }

    getGroup2Enabled(): boolean | undefined {
        return this.groups?.group2?.enabled;
    }

    getGroup1UsersEnabled(): boolean | undefined {
        return this.groups?.group1?.group1UsersEnabled;
    }

    getGroup2UsersEnabled(): boolean | undefined {
        return this.groups?.group2?.group2UsersEnabled;
    }

    getGroup1ContactEnabled(): boolean | undefined {
        return this.groups?.group1?.group1ContactsEnabled;
    }

    getGroup2ContactEnabled(): boolean | undefined {
        return this.groups?.group2?.group2ContactsEnabled;
    }

    getGroup1JobsEnabled(): boolean | undefined {
        return this.groups?.group1?.group1JobsEnabled;
    }

    getGroup2JobsEnabled(): boolean | undefined {
        return this.groups?.group2?.group2JobsEnabled;
    }

    getGroup1Caption(language: string): string {
        let title = (this.groups?.group1 as any)[language];
        if (!title) title = this.groups?.group1?.["en-CA"] ?? "";
        return title;
    }

    getGroup2Caption(language: string): string {
        let title = (this.groups?.group2 as any)[language];
        if (!title) title = this.groups?.group2?.["en-CA"] ?? "";
        return title;
    }

    getGroup1Options(includeHidden = true): IGroupOptionModel[] | undefined {
        return this.groups?.group1Options.filter((option) => includeHidden || option.IsVisible);
    }

    getGroup2Options(includeHidden = true): IGroupOptionModel[] | undefined {
        return this.groups?.group2Options.filter((option) => includeHidden || option.IsVisible);
    }

    getGroupOptionCaption(groupObj: IGroupOptionModel[] | undefined, id: number, language: string): string {
        if (groupObj) {
            for (const group of groupObj) {
                if (group.Id == id) return (group as any)[language] ?? "";
            }
        }
        return "";
    }

    getGroup1OptionCaption(id: number, language: string): string {
        return this.getGroupOptionCaption(this.getGroup1Options(), id, language);
    }

    getGroup2OptionCaption(id: number, language: string): string {
        return this.getGroupOptionCaption(this.getGroup2Options(), id, language);
    }
}
